import styles from './SideNavigationMain.module.scss';
import {IconDoubleLeftChevron, IconDoubleRightChevron} from '../../icons';
import {useSideNavigationCollapseContext} from '../side-navigation-contexts/SideNavigationCollapseContext';
import {SideNavigationLogo} from '../../logos';
import React, {PropsWithChildren} from 'react';
import classNames from 'classnames';
import {SideNavigationSearch} from '../side-navigation-search/SideNavigationSearch';
import {SideNavigationCTASection} from '../side-navigation-cta/SideNavigationCTA';

export type SideNavigationMainNavProps = {
    /** The logo that is displayed at the top of the navigation. */
    Logo: SideNavigationLogo['Logo'];
    /** The action that is performed when the logo is clicked. */
    onLogoClick?: SideNavigationLogo['onClick'];
};

// main nav contains: Logo, Collapse Button, Search Bar, CTA Buttons
export const SideNavigationMain = ({Logo, onLogoClick, children}: PropsWithChildren<SideNavigationMainNavProps>) => {

    const [searchChild, ctasChild] = React.Children.toArray(children);

    const {isCollapsed, handleCollapse} = useSideNavigationCollapseContext();
    const collapseButtonIcon = isCollapsed ? <IconDoubleRightChevron/> : <IconDoubleLeftChevron/>;

    const mainNavContainerClasses = classNames(
        styles.mainNavContainer,
        {[styles.collapsed]: isCollapsed}
    );

    const logoAndCollapseContainerStyling = classNames(
        styles.unsetButton,
        styles.logoAndCollapseContainer,
        {[styles.collapsed]: isCollapsed},
        {[styles.expanded]: !isCollapsed},
    );

    const collapseButtonStyles = classNames(
        styles.collapseButton,
        {[styles.expanded]: !isCollapsed},
    );

    const searchBarAndLogoContainerStyling = classNames(
        styles.searchBarAndLogoContainer,
        {[styles.collapsed]: isCollapsed},
        {[styles.expanded]: !isCollapsed},
    );

    const logoToDisplay = (
        isCollapsed ? <></> :
            <button tabIndex={0} aria-label={'Site Logo'}
                    className={`${styles.logoButton} ${onLogoClick && styles.logoPointer}`} onClick={onLogoClick}>
                <Logo/>
            </button>
    );

    return (
        <div data-collapsed={isCollapsed} className={mainNavContainerClasses}>
            <div className={searchBarAndLogoContainerStyling}>
                <div className={logoAndCollapseContainerStyling}>
                    {logoToDisplay}
                    <button
                        aria-label={isCollapsed ? 'Expand side navigation container' : 'Collapse side navigation container'}
                        tabIndex={0} className={collapseButtonStyles} onClick={handleCollapse}>
                        {collapseButtonIcon}
                    </button>
                </div>
                {searchChild}
            </div>
            {ctasChild}
        </div>
    );
}

SideNavigationMain.Search = SideNavigationSearch;
SideNavigationMain.CTA = SideNavigationCTASection;